import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage, Button, Notice } from '../components/ui'
import { formatLabel, providerLabel } from '../lib/format'

// One card per thing a manager can do from here. Kept as a component rather
// than three copies of the same markup because the only difference between them
// is the words, and the moment a fourth arrives — player rankings — it should
// cost one entry in a list, not another block of Tailwind.
function Action({ to, title, children, badge, disabled = false, disabledReason }) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-slate-100">{title}</span>
        {badge && (
          <span className="shrink-0 rounded-full border border-slate-600 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
            {badge}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">
        {disabled ? disabledReason : children}
      </p>
    </>
  )

  if (disabled) {
    return (
      <div
        aria-disabled="true"
        className="rounded-md border border-slate-800 bg-slate-950/50 px-3 py-2.5 opacity-60"
      >
        {body}
      </div>
    )
  }

  return (
    <Link
      to={to}
      className="block rounded-md border border-slate-700 px-3 py-2.5 transition hover:border-slate-500 hover:bg-slate-800/40"
    >
      {body}
    </Link>
  )
}

export default function League() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Same ['league', id] key the builder and explorer use, so arriving here
  // warms the cache for wherever the manager goes next.
  const league = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => api.league(leagueId),
  })

  const sync = useMutation({
    mutationFn: () => api.syncLeague(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagueSuggestions', leagueId] })
    },
  })

  // Which team is yours. Detected on import when the platform can say; a public
  // ESPN import has no cookies and so nobody to match, and without a pick the
  // builders quietly start from the first team — someone else's roster.
  const [changingTeam, setChangingTeam] = useState(false)
  const pickTeam = useMutation({
    mutationFn: (teamId) => api.setMyTeam(leagueId, teamId),
    onSuccess: () => {
      setChangingTeam(false)
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] })
      queryClient.invalidateQueries({ queryKey: ['leagueSuggestions', leagueId] })
    },
  })

  if (league.isPending) {
    return (
      <Layout>
        <Spinner label="Loading league…" />
      </Layout>
    )
  }

  if (league.error) {
    return (
      <Layout>
        <ErrorMessage error={league.error} />
      </Layout>
    )
  }

  const info = league.data.league
  const teams = league.data.teams || []
  const health = info.data_health
  const currentWeek = league.data.current_week

  const myTeam = teams.find((team) => team.id === info.my_team_id)
  const choosingTeam = teams.length > 0 && (changingTeam || !myTeam)
  // Rosters are what every one of these actions reads. Without them the pages
  // below render empty controls and no explanation, so the choice is made here
  // instead of three times over.
  const ready = health ? health.ready : true

  return (
    <Layout>
      <div className="space-y-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/leagues')}
            className="text-xs text-slate-400 hover:text-slate-200"
          >
            ← All leagues
          </button>

          <h1 className="mt-1.5 text-lg font-semibold text-slate-100">{info.name}</h1>
          <p className="text-xs text-slate-400">
            {info.season} · {info.num_teams} teams · {formatLabel(info.format?.ppr_type)}
            {info.format?.superflex ? ' · Superflex' : ''}
            {info.format?.dynasty ? ' · Dynasty' : ''}
            {currentWeek ? ` · Week ${currentWeek}` : ''}
          </p>
          {myTeam && (
            <p className="mt-0.5 text-xs text-slate-400">
              You are <span className="text-slate-200">{myTeam.name}</span>
              {myTeam.owner ? ` (${myTeam.owner})` : ''}
              {' · '}
              <button
                type="button"
                onClick={() => setChangingTeam(!changingTeam)}
                className="underline hover:text-slate-200"
              >
                {changingTeam ? 'Keep it' : 'Change'}
              </button>
            </p>
          )}
        </div>

        {!myTeam && teams.length > 0 && (
          <Notice title="Which team is yours?">
            {providerLabel(info.provider)} didn't say, so pick it under Teams below. Trades and
            suggestions start from your roster, and until you choose they start from the first
            team in the league.
          </Notice>
        )}

        {health && !health.rosters_present && (
          <Notice
            title="No rosters synced yet"
            action={
              <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </Button>
            }
          >
            Nothing here can be worked out without rosters. Syncing pulls them from{' '}
            {providerLabel(info.provider)}.
          </Notice>
        )}

        {health && health.rosters_present && !health.projections_present && (
          <Notice title={`No projections synced for week ${health.projected_week}`}>
            Trades can be built, but every number would come back as zero until{' '}
            <code className="text-amber-200">bin/rails sleeper:bootstrap</code> has run.
          </Notice>
        )}

        {/* The silent one, and the reason it is shown at all. Projections are
            Rotowire's, delivered by Sleeper and keyed on its player ids, so a
            player this app could not match to a Sleeper id has no projection and
            can never have one — and Scoring::Engine reads that absence as 0.0
            with no error. A trade involving one of these is not approximately
            right, it is confidently wrong, and it looks identical to a trade
            that is fine. */}
        {health && health.unpriceable_players > 0 && (
          <Notice title={`${health.unpriceable_players} rostered ${health.unpriceable_players === 1 ? 'player has' : 'players have'} no projections`}>
            {providerLabel(info.provider)} lists{' '}
            {health.unpriceable_players === 1 ? 'a player' : 'players'} this app could not match to
            the projection data, usually a rookie or a recent signing.{' '}
            {health.unpriceable_players === 1 ? 'It counts' : 'They count'} as{' '}
            <strong className="text-amber-200">zero points</strong> in every trade, so a deal
            involving {health.unpriceable_players === 1 ? 'that player' : 'one of them'} will read
            as worse than it is. Everything else on this league is unaffected.
          </Notice>
        )}

        <ErrorMessage error={sync.error} />

        <Card
          title="What do you want to do?"
          subtitle="Suggestions are free to browse. Evaluating a trade is what counts against the meter."
        >
          <div className="space-y-2">
            <Action
              to={`/leagues/${leagueId}/suggestions`}
              title="See trade suggestions"
              badge="Free"
              disabled={!ready}
              disabledReason="Needs rosters and projections before it can suggest anything."
            >
              Deals worth proposing, ranked — what each one does for you, and whether the
              other manager has a reason to say yes.
            </Action>

            <Action
              to={`/leagues/${leagueId}/trade`}
              title="Build a trade"
              disabled={!ready}
              disabledReason="Needs rosters and projections before a trade can be evaluated."
            >
              Pick the players yourself and get the full verdict: points by position, win
              probability week by week, and what the trade cost on draft day.
            </Action>

            <Action
              to={`/leagues/${leagueId}/explore`}
              title="Explore a what-if"
              disabled={!ready}
              disabledReason="Needs rosters and projections before a roster can be modelled."
            >
              Drop any player in the league onto any roster to see what your team would look
              like with them. A sandbox, not a proposal.
            </Action>
          </div>
        </Card>

        <Card
          title="Teams"
          subtitle={choosingTeam ? 'Tap the team that is yours.' : `${teams.length} in this league`}
        >
          <ErrorMessage error={pickTeam.error} />

          <ul className="grid gap-1.5 sm:grid-cols-2">
            {teams.map((team) => {
              const mine = team.id === info.my_team_id
              const tone = mine
                ? 'border-emerald-700 bg-emerald-950/30 text-slate-100'
                : 'border-slate-800 text-slate-300'
              const label = (
                <>
                  <span className="block truncate">{team.name}</span>
                  {team.owner && team.owner !== team.name && (
                    <span className="block truncate text-xs text-slate-500">{team.owner}</span>
                  )}
                </>
              )

              return (
                <li key={team.id}>
                  {choosingTeam ? (
                    <button
                      type="button"
                      onClick={() => pickTeam.mutate(team.id)}
                      disabled={pickTeam.isPending}
                      aria-pressed={mine}
                      className={`w-full rounded-md border px-2.5 py-1.5 text-left text-sm hover:border-slate-500 disabled:opacity-60 ${tone}`}
                    >
                      {label}
                    </button>
                  ) : (
                    <div className={`rounded-md border px-2.5 py-1.5 text-sm ${tone}`}>{label}</div>
                  )}
                </li>
              )
            })}
          </ul>

          {/* Following a league you don't play in is a real use, and the
              honest answer to "which team is yours" is then none of them. */}
          {changingTeam && myTeam && (
            <button
              type="button"
              onClick={() => pickTeam.mutate(null)}
              disabled={pickTeam.isPending}
              className="mt-2 text-xs text-slate-400 underline hover:text-slate-200 disabled:opacity-60"
            >
              None of these is mine
            </button>
          )}
        </Card>

        <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
          <span>
            {info.last_synced_at
              ? `Last synced ${new Date(info.last_synced_at).toLocaleString()}`
              : 'Never synced'}
          </span>
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            className="text-slate-400 hover:text-slate-200 disabled:opacity-60"
          >
            {sync.isPending ? 'Syncing…' : 'Sync league'}
          </button>
        </div>
      </div>
    </Layout>
  )
}
